import React from 'react';
import { useDispatch } from 'react-redux';

import {
  InputWithConfirm,
  SelectWithConfirm,
  TextareaWithConfirm,
  CustomHeading,
  CheckboxOptionsWithConfirm,
} from 'shared-components';

import QuestionPage from '../../../templates/question';
import { Q } from '../../../../store/student/types';
import { ICheckbox } from '../../../../types';
import { savePreCasInterview } from '../../../../store/student/actions';
import { createUpdatePCIFieldAction } from '../../../../store/student/preCasInterview/actions';
import { Pages } from '../../../../store/student/preCasInterview/types';
import { useCustomNavigate } from '../../../../hooks/useCustomNavigate';

export interface IProps {
  complete: boolean;
  currentStep: number;
  steps: number;
  whyUk: string;
  whyUkOther: string;
  selectedCourseReasons: string[];
  selectedCourseReasonsOther: string;
  selectedUniReasons: string[];
  selectedUniReasonsOther: string;
  uniChoiceReason1: string;
  uniChoiceReason2: string;
  uniChoiceReason3: string;
  pageConfig: any;
}

type Props = IProps;

const InterviewQPage1 = (props: Props) => {
  const dispatch = useDispatch();
  const onNext = () => {
    dispatch(savePreCasInterview(Pages.PAGE1));
  };
  const onChange = (fieldName: string, value: any, refValue?: any) =>
    dispatch(createUpdatePCIFieldAction(fieldName, value, refValue));

  const { navigateToStudentDashboard } = useCustomNavigate();
  const onBackToTaskList = () => navigateToStudentDashboard();

  // TODO: refactor and remove if not needed
  const whyUkError = '';
  const whyUkOtherError = '';
  const courseReasonsError = '';
  const uniChoiceReason1Error = '';
  const uniChoiceReason2Error = '';
  const uniChoiceReason3Error = '';

  const {
    whyUk,
    whyUkOther,
    selectedCourseReasons,
    selectedCourseReasonsOther,
    selectedUniReasons,
    selectedUniReasonsOther,
    uniChoiceReason1,
    uniChoiceReason2,
    uniChoiceReason3,
  } = props;

  const getWhyUkOptions = () => {
    const options = [
      'High quality of education',
      'Time alone to focus on study',
      'Experience in a new culture',
      'Positive impression after travel to the country',
      'Family ties to the country',
      'Recommendation (e.g. Friend or family)',
    ];

    return options.map(option => ({
      label: option,
      value: option,
    }));
  };

  const getCourseReasons = (): ICheckbox[] => {
    const courseReasons = [
      'Aligned with my career goals',
      'Course content (modules)',
      'Recommendation (e.g. Friend or family)',
      'I am interested in this subject area',
      'The course follows on from my previous study',
      'Other',
      'I am not sure why I chose this course',
    ];

    return courseReasons.map((reason: string, _index: number) => ({
      id: reason,
      label: reason,
      isTicked: !!(props.selectedCourseReasons && props.selectedCourseReasons.includes(reason)),
      readOnly: false,
    }));
  };

  const getUniReasons = (): ICheckbox[] => {
    const uniReasons = [
      'University Reputation/Ranking',
      'Course Reputation/Ranking',
      'University Location',
      'Cost (e.g. affordabilty)',
      'Recommendation (e.g. Friend or family)',
      'University Facilities',
      'Other',
      'I am not sure why I chose this university',
    ];

    return uniReasons.map((reason: string, _index: number) => ({
      id: reason,
      label: reason,
      isTicked: !!(props.selectedUniReasons && props.selectedUniReasons.includes(reason)),
      readOnly: false,
    }));
  };

  const onCourseReasonsChange = (selectedReason: string, isTicked: boolean) => {
    let selectedCourseReasons = [];
    const currentCourseReasons = props.selectedCourseReasons.slice(0);

    if (!isTicked) {
      // remove from list
      selectedCourseReasons = currentCourseReasons.filter((reason: string) => {
        return reason !== selectedReason;
      });
    } else {
      // add to list
      currentCourseReasons.push(selectedReason);
      selectedCourseReasons = currentCourseReasons.slice(0);
    }
    onChange(Q.SELECTED_COURSE_REASONS, selectedCourseReasons);
  };

  const onUniReasonsChange = (selectedReason: string, isTicked: boolean) => {
    let selectedUniReasons = [];
    const currentUniReasons = props.selectedUniReasons.slice(0);

    if (!isTicked) {
      // remove from list
      selectedUniReasons = currentUniReasons.filter((reason: string) => {
        return reason !== selectedReason;
      });
    } else {
      // add to list
      currentUniReasons.push(selectedReason);
      selectedUniReasons = currentUniReasons.slice(0);
    }
    onChange(Q.SELECTED_UNI_REASONS, selectedUniReasons);
  };
  return (
    <QuestionPage
      complete={props.complete}
      currentStep={props.currentStep}
      steps={props.steps}
      onNext={onNext}
      onBackToTaskList={onBackToTaskList}
      section={'Pre-CAS Questionnaire'}
    >
      <SelectWithConfirm
        key={Q.WHY_UK}
        options={getWhyUkOptions()}
        label="Why did you choose to study in the UK?"
        inputHelperText={
          whyUk === 'Other' || whyUk === 'I am not sure why I chose to study in the UK'
            ? props.pageConfig.WhyUKOther?.helpText
            : props.pageConfig.WhyUK?.helpText
        }
        errorText={whyUkError || whyUkOtherError}
        otherOptions={[
          {
            label: 'Other',
            value: 'Other',
          },
          {
            label: 'I am not sure why I chose to study in the UK',
            value: 'I am not sure why I chose to study in the UK',
          },
        ]}
        otherOptionLabel="Please provide more information"
        value={whyUk}
        onChange={(evt: any) => onChange(Q.WHY_UK, evt.currentTarget.value)}
        onChangeOtherText={(evt: any) => onChange(Q.WHY_UK_OTHER, evt.currentTarget.value)}
        otherValue={whyUkOther}
      />

      <CheckboxOptionsWithConfirm
        id="course-reasons"
        key={Q.SELECTED_COURSE_REASONS}
        inputHelperText={props.pageConfig.CourseReasons?.helpText}
        errorText={courseReasonsError}
        checkboxes={getCourseReasons()}
        labelText="Why did you choose this course?"
        onChange={onCourseReasonsChange}
      />

      {selectedCourseReasons &&
        (selectedCourseReasons.findIndex(reason => reason === 'Other') > -1 ||
          selectedCourseReasons.findIndex(reason => reason === 'I am not sure why I chose this course') > -1) && (
          <InputWithConfirm
            key={Q.SELECTED_COURSE_REASONS_OTHER}
            value={selectedCourseReasonsOther}
            label={'Please provide more information'}
            inputHelperText={props.pageConfig.CourseReasonsOther?.helpText}
            onChange={(evt: any) => onChange(Q.SELECTED_COURSE_REASONS_OTHER, evt.currentTarget.value)}
          />
        )}

      <CheckboxOptionsWithConfirm
        id="uni-reasons"
        key={Q.SELECTED_UNI_REASONS}
        inputHelperText={props.pageConfig.UniReasons?.helpText}
        errorText={courseReasonsError}
        checkboxes={getUniReasons()}
        labelText="Why did you choose this university?"
        onChange={onUniReasonsChange}
      />

      {selectedUniReasons &&
        (selectedUniReasons.findIndex(reason => reason === 'Other') > -1 ||
          selectedUniReasons.findIndex(reason => reason === 'I am not sure why I chose this university') > -1) && (
          <InputWithConfirm
            key={Q.SELECTED_UNI_REASONS_OTHER}
            value={selectedUniReasonsOther}
            label={'Please provide more information'}
            inputHelperText={props.pageConfig.UniReasonsOther?.helpText}
            onChange={(evt: any) => onChange(Q.SELECTED_UNI_REASONS_OTHER, evt.currentTarget.value)}
          />
        )}

      <CustomHeading level="h3">
        In your own words, please provide three reasons you chose this university?
      </CustomHeading>

      <TextareaWithConfirm
        id="uni-choice-reason-1"
        key={Q.UNI_CHOICE_REASON_1}
        value={uniChoiceReason1}
        label="Reason 1"
        inputHelperText={props.pageConfig.UniChoiceReason1?.helpText}
        errorText={uniChoiceReason1Error}
        onChange={(event: React.FormEvent<HTMLTextAreaElement>) =>
          onChange(Q.UNI_CHOICE_REASON_1, event.currentTarget.value)
        }
      />

      <TextareaWithConfirm
        id="uni-choice-reason-2"
        key={Q.UNI_CHOICE_REASON_2}
        value={uniChoiceReason2}
        label="Reason 2"
        inputHelperText={props.pageConfig.UniChoiceReason2?.helpText}
        errorText={uniChoiceReason2Error}
        onChange={(event: React.FormEvent<HTMLTextAreaElement>) =>
          onChange(Q.UNI_CHOICE_REASON_2, event.currentTarget.value)
        }
      />

      <TextareaWithConfirm
        id="uni-choice-reason-3"
        key={Q.UNI_CHOICE_REASON_3}
        value={uniChoiceReason3}
        label="Reason 3"
        inputHelperText={props.pageConfig.UniChoiceReason3?.helpText}
        errorText={uniChoiceReason3Error}
        onChange={(event: React.FormEvent<HTMLTextAreaElement>) =>
          onChange(Q.UNI_CHOICE_REASON_3, event.currentTarget.value)
        }
      />
    </QuestionPage>
  );
};

export default InterviewQPage1;
